import { Stack } from "@mui/material";
import ManagementsRequestState from "../../../Managements/ManagementsRequestState/ManagementsRequestState";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  ManagementDetail,
  NewDocumentData,
  RequestStateDescriptionData,
} from "../../../../core/models/managements/managements.model";
import {
  formatManagementDetail,
  getManagementsRequestTypeData,
  incedenceTexts,
  initialManagementDetail,
} from "../../../../utils/components/managements/managements.util";
import {
  getCallCenterDocuments,
  getManagementDetail as getManagementDetailFetch,
} from "../../../../core/services/managements/managements.service";
import {
  MANAGEMENTS_REGISTER_TYPE_CLAIMS,
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT,
} from "../../../../core/constants/constants";
import TotalEnergiesContext from "../../../../core/store/context/TotalEnergiesContext";
import theme from "../../../../muiTheme";

interface SuppliesDetailHistoricManagementsProps {
  idIssue: number;
  recordTextType: string;
  claimTextSubType: string;
  incidenceText: string;
}

const SuppliesDetailHistoricManagements = ({
  idIssue,
  recordTextType,
  claimTextSubType,
  incidenceText,
}: SuppliesDetailHistoricManagementsProps) => {
  const { userData, isAgent } = useContext(TotalEnergiesContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>({
      title: "",
      description: "",
    });
  const [hasDocuments, setHasDocuments] = useState<boolean>(false);
  const [managementDetail, setManagementDetail] = useState<ManagementDetail>(
    initialManagementDetail
  );
  const [filesAttached, setFilesAttached] = useState<NewDocumentData[]>([]);
  const [refferenceNumber, setRefferenceNumber] = useState<number>(0);
  const showManagementRequestInfo = ![
    MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
  ].includes(claimTextSubType);
  const isUpdatePowersManagement =
    MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT === claimTextSubType;

  const getManagementDetail = useCallback(
    async (
      idIssue: number,
      token: string,
      claimTextSubType: string,
      isUpdatePowersManagement: boolean,
      abortSignal: AbortSignal
    ) => {
      await getManagementDetailFetch(idIssue, token, abortSignal)
        .then(async (managementDetail) => {
          let formattedManagementDetail = initialManagementDetail;
          if (Array.isArray(managementDetail) && managementDetail.length > 0) {
            formattedManagementDetail = formatManagementDetail(
              managementDetail[0]
            );
          } else {
            formattedManagementDetail =
              formatManagementDetail(managementDetail);
          }

          setManagementDetail(formattedManagementDetail);

          //Set has documents to download
          if (
            formattedManagementDetail.documents.some(
              (document) => document.FileName != null
            )
          ) {
            setHasDocuments(true);
            setRefferenceNumber(
              formattedManagementDetail.refferenceNumber
                ? formattedManagementDetail.refferenceNumber
                : 0
            );
          } else {
            setHasDocuments(false);
          }

          const incidenceDetailText = incedenceTexts.find(
            (incidenceMatchingText) =>
              incidenceText.includes(incidenceMatchingText)
          );
          const claimText =
            formattedManagementDetail.registerType ===
            MANAGEMENTS_REGISTER_TYPE_CLAIMS
              ? MANAGEMENTS_REGISTER_TYPE_CLAIMS
              : undefined;

          setRequestStateData(
            getManagementsRequestTypeData(
              claimText ?? incidenceDetailText ?? claimTextSubType,
              formattedManagementDetail.state,
              isAgent ?? false
            )
          );
          setIsLoading(false);
        })
        .catch((error) => {
          setManagementDetail((prevManagementDetail) => ({
            ...prevManagementDetail,
            state: MANAGEMENTS_STATE_ERROR,
          }));
          setRequestStateData(
            getManagementsRequestTypeData(
              claimTextSubType,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false
            )
          );
          setIsLoading(false);
        });
    },
    [incidenceText]
  );

  useEffect(() => {
    const managementDetailAbortSignal = new AbortController();
    if (userData && idIssue) {
      setIsLoading(true);
      getManagementDetail(
        +idIssue,
        userData.token,
        claimTextSubType,
        isUpdatePowersManagement,
        managementDetailAbortSignal.signal
      );

      return () => managementDetailAbortSignal.abort();
    }
  }, [
    claimTextSubType,
    isUpdatePowersManagement,
    getManagementDetail,
    recordTextType,
    userData,
    incidenceText,
    idIssue,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (hasDocuments && refferenceNumber !== 0) {
        setIsLoadingFiles(true);
        if (userData) {
          try {
            const response = await getCallCenterDocuments(
              refferenceNumber,
              userData.token
            );

            setFilesAttached((prevFiles) => {
              const newDocuments = response.filter(
                (doc) =>
                  !prevFiles.some(
                    (existingDoc) => existingDoc.IdDocumento === doc.IdDocumento
                  )
              );
              return [...prevFiles, ...newDocuments];
            });
          } catch (error) {
            console.error("Error downloading document:", error);
          }
          setIsLoadingFiles(false);
        }
      }
    };

    fetchData();
  }, [hasDocuments, refferenceNumber, userData]);

  return (
    <Stack
      component="article"
      direction="column"
      justifyContent="center"
      alignItems="center"
      bgcolor={theme.palette.grey[50]}
      paddingBottom={"1rem"}
      marginBottom={{ xs: "1.25rem", md: 0 }}
      borderRadius={"0.75rem"}
    >
      <ManagementsRequestState
        showManagementRequestInfo={showManagementRequestInfo}
        isLoading={isLoading}
        state={managementDetail.state}
        managementRequestInfo={managementDetail}
        title={requestStateData.title}
        description={requestStateData.description}
        descriptionExtra={requestStateData.descriptionExtra}
        loadingFiles={isLoadingFiles}
        files={filesAttached}
        isSupplies={true}
      />
    </Stack>
  );
};

export default SuppliesDetailHistoricManagements;
